import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import BulkActionsSettingsAPI from '../../api/bulkActionsSettings';
import { throwErrorMessage } from '../utils/api';

export const state = {
    records: [],
    preview: null,
    report: null,
    uiFlags: {
        isFetching: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isPreviewing: false,
        isFetchingReport: false,
    },
};

export const getters = {
    getBulkActions(_state) {
        return _state.records;
    },
    getBulkActionById: _state => id => {
        return _state.records.find(record => record.id === Number(id));
    },
    getPreview(_state) {
        return _state.preview;
    },
    getReport(_state) {
        return _state.report;
    },
    getUIFlags(_state) {
        return _state.uiFlags;
    },
};

export const actions = {
    get: async ({ commit }, params = {}) => {
        commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isFetching: true });
        try {
            const response = await BulkActionsSettingsAPI.get(params);
            commit(types.SET_BULK_ACTIONS_SETTINGS, response.data.payload || []);
        } catch (error) {
            throwErrorMessage(error);
        } finally {
            commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isFetching: false });
        }
    },
    create: async ({ commit }, payload) => {
        commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isCreating: true });
        try {
            const response = await BulkActionsSettingsAPI.create(payload);
            commit(types.ADD_BULK_ACTIONS_SETTINGS, response.data.payload || response.data);
            return response.data.payload || response.data;
        } catch (error) {
            throwErrorMessage(error);
            return null;
        } finally {
            commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isCreating: false });
        }
    },
    update: async ({ commit }, { id, payload }) => {
        commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isUpdating: true });
        try {
            const response = await BulkActionsSettingsAPI.update(id, payload);
            const updated = response.data.payload || response.data;
            commit(types.EDIT_BULK_ACTIONS_SETTINGS, updated);
            return updated;
        } catch (error) {
            throwErrorMessage(error);
            return null;
        } finally {
            commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isUpdating: false });
        }
    },
    delete: async ({ commit }, id) => {
        commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isDeleting: true });
        try {
            await BulkActionsSettingsAPI.delete(id);
            commit(types.DELETE_BULK_ACTIONS_SETTINGS, id);
            return true;
        } catch (error) {
            throwErrorMessage(error);
            return false;
        } finally {
            commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isDeleting: false });
        }
    },
    preview: async ({ commit }, payload) => {
        commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isPreviewing: true });
        try {
            const response = await BulkActionsSettingsAPI.preview(payload);
            const preview = response.data.payload || response.data;
            commit(types.SET_BULK_ACTIONS_SETTINGS_PREVIEW, preview);
            return preview;
        } catch (error) {
            throwErrorMessage(error);
            return null;
        } finally {
            commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isPreviewing: false });
        }
    },
    getReport: async ({ commit }, id) => {
        commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, { isFetchingReport: true });
        try {
            const response = await BulkActionsSettingsAPI.report(id);
            const report = response.data.payload || response.data;
            commit(types.SET_BULK_ACTIONS_SETTINGS_REPORT, report);
            return report;
        } catch (error) {
            throwErrorMessage(error);
            return null;
        } finally {
            commit(types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG, {
                isFetchingReport: false,
            });
        }
    },
    clearPreview: ({ commit }) => {
        commit(types.SET_BULK_ACTIONS_SETTINGS_PREVIEW, null);
    },
    clearReport: ({ commit }) => {
        commit(types.SET_BULK_ACTIONS_SETTINGS_REPORT, null);
    },
};

export const mutations = {
    [types.SET_BULK_ACTIONS_SETTINGS_UI_FLAG](_state, data) {
        _state.uiFlags = {
            ..._state.uiFlags,
            ...data,
        };
    },
    [types.SET_BULK_ACTIONS_SETTINGS]: MutationHelpers.set,
    [types.ADD_BULK_ACTIONS_SETTINGS]: MutationHelpers.create,
    [types.EDIT_BULK_ACTIONS_SETTINGS]: MutationHelpers.update,
    [types.DELETE_BULK_ACTIONS_SETTINGS]: MutationHelpers.destroy,
    [types.SET_BULK_ACTIONS_SETTINGS_PREVIEW](_state, payload) {
        _state.preview = payload;
    },
    [types.SET_BULK_ACTIONS_SETTINGS_REPORT](_state, payload) {
        _state.report = payload;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
